import { useState, useMemo, useEffect } from "react";

// Import hooks and utils
import { generateVariantCombos } from "../utils/generateVariantCombos";
import { generateCustomId } from "@/utils/generateCustomId";
import { mergeCombosWithPreservedData } from "../utils/mergeComboWithPreservedData";

// Import components
import VariantComboControls from "./VariantComboControls";
import VariantComboTableHeader from "./VariantComboTableHeader";
import VariantTableRows from "./VariantTableRows";
import VariantEditModal from "./VariantEditModal";

export default function VariantComboTable ({ variants = [], variantMap = null, productPricingDefaults = null }) {
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState([]);
  const [activeVariant, setActiveVariant] = useState(null);

  // Track an explicit image chosen for a variant group
  const [groupImages, setGroupImages] = useState({});

  const defaultImage = "/images/default-img.jpg";

  // Determine mode and generate raw combos
  const isEditMode = variants.length > 0;

  const rawCombos = useMemo(() => {
    if (isEditMode) return variants;
    if (variantMap) return generateVariantCombos(variantMap);
    return [];
  }, [isEditMode, variantMap]);

  const [comboList, setComboList] = useState([]);

  // Extract keys from the first combo
  const optionKeys = rawCombos?.[0] ? Object.keys(rawCombos[0].options) : [];
  const groupBy = optionKeys[0] || null;
  const subOptionKey = optionKeys[1] || null;
  const isSingleOption = optionKeys.length === 1;

  // Map rawCombos to fully usable combos
  useEffect(() => {
    const defaults = productPricingDefaults || {};
    const incoming = rawCombos.map((combo) => ({
      ...combo,
      variantId: isEditMode ? (combo.id ?? combo.variantId ?? null) : null,
      variantUiId: combo.variantUiId || generateCustomId({ prefix: "variant-" }),
      subVariantName: subOptionKey
        ? combo.options[subOptionKey]
        : combo.options[groupBy],
      price: isEditMode ? combo.price : (defaults.price ?? ""),
      compareAtPrice: isEditMode ? combo.compareAtPrice : (defaults.compareAtPrice ?? ""),
      costPerItem: isEditMode ? combo.costPerItem : (defaults.costPerItem ?? ""),
      inventoryQuantity: isEditMode ? combo.inventoryQuantity : "",
      sku: isEditMode ? combo.sku : "",
      barcode: isEditMode ? combo.barcode : "",
      trackInventory: isEditMode ? (combo.trackInventory ?? true) : true,
      imageOverride: isEditMode ? combo.imageOverride : "",
      meta: isEditMode ? (combo.meta || {}) : {},
    }));

    setComboList((prev) => mergeCombosWithPreservedData(incoming, prev));
  }, [rawCombos, isEditMode, subOptionKey, groupBy, productPricingDefaults]);

  const groupedCombos = comboList.reduce((acc, combo) => {
    const groupKey = combo.options[groupBy];
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(combo);
    return acc;
  }, {});

  const toggleExpand = (groupKey) => {
    setExpanded((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const areAllSelected = (comboList) =>
    comboList.every((combo) => selected.includes(combo.variantUiId));

  const areSomeSelected = (comboList) =>
    comboList.some((combo) => selected.includes(combo.variantUiId)) &&
    !areAllSelected(comboList);

  const totalInventory = comboList.reduce(
    (acc, curr) => acc + (parseInt(String(curr.inventoryQuantity).replace(/,/g, "")) || 0),
    0
  );

  if (!groupBy) return null;

  return (
    <div className="mt-[2em] border border-[#e3e3e3] rounded-[5px] flex flex-col">
      {/* Controls row */}
      <VariantComboControls />

      {/* Table Headers */}
      <VariantComboTableHeader
        selected={selected}
        combos={comboList}
        setSelected={setSelected}
      />

      {/* Table contsnt */}
      <VariantTableRows
        comboList={comboList || []}
        setComboList={setComboList}
        groupedCombos={groupedCombos}
        isSingleOption={isSingleOption}
        groupBy={groupBy}
        expanded={expanded}
        selected={selected}
        setSelected={setSelected}
        toggleExpand={toggleExpand}
        toggleSelect={toggleSelect}
        defaultImage={defaultImage}
        groupImages={groupImages}
        setGroupImages={setGroupImages}
        setActiveVariant={setActiveVariant}
      />

      {/* Total Inventory summary */}
      <div className="bg-[#efefef] flex items-center justify-center">
        <p className="font-medium py-[0.6em] text-[15px]">
          Total Inventory at shop: <span className="text-black">{totalInventory.toLocaleString()} available</span>
        </p>
      </div>

      {/* Render the variant edit modal */}
      {activeVariant && (
        <VariantEditModal
          variant={activeVariant}
          onClose={() => setActiveVariant(null)}
          onSave={(updatedData) => {
            setComboList((prev) =>
              prev.map((combo) =>
                combo.variantUiId === updatedData.variantUiId
                  ? { ...combo, ...updatedData }
                  : combo
              )
            );
            setActiveVariant(null);
          }}
        />
      )}
    </div>
  );
};
